import { createPulseTracker } from '@schibsted/pulse-sdk';
import { tcf, scc } from '@schibsted/sourcepoint';
import { datadogLogs } from '@datadog/browser-logs';
import { version } from '../package.json';

const SKIP_CONSENT_ROUTES = [
    CONFIG.subPagesPaths?.sharing?.login,
];

const shouldSkipConsentInit = () =>
    SKIP_CONSENT_ROUTES.some(route => window.location.pathname.includes(route));

const createActorBuilder = getState => () => {
    const state = getState?.();
    const userId = state?.spid?.userId;

    if (!userId) {
        return null;
    }

    return {
        id: String(userId),
        realm: CONFIG.tracking.spidRealm,
    };
};

const normalizeStage = (stage) => {
    const stageMap = {
        dev: 'Dev',
        pre: 'Pre',
        pro: 'Pro',
    };

    return stageMap[stage?.toLowerCase()] || 'Dev';
};

const initTracking = (service, config, initFn) => {
    if (!config || shouldSkipConsentInit()) return;

    const { baseEndpoint,
            propertyId,
            consentLanguage,
            groupPmId,
            propertyHref,
            adChoicesSubscriberPmId,
            enableAdChoicesFeature,
            targetingParams } = config;

    try {
        initFn(window, document, navigator, {
            baseEndpoint,
            propertyId,
            consentLanguage,
            groupPmId,
            propertyHref,
            ...(adChoicesSubscriberPmId ? { adChoicesSubscriberPmId } : {}),
            enableAdChoicesFeature: !!enableAdChoicesFeature,
            ...(targetingParams ? { targetingParams } : {}),
        });
    } catch (error) {
        console.error(`Error initializing ${service}`, error); // eslint-disable-line no-console
        datadogLogs.logger.warn(`Error initializing ${service}`, { config }, error);
    }
};

const createTracker = async (config, getState) => {
    const actorBuilder = createActorBuilder(getState);

    if (config.tcfTracking && config.tcfTracking.baseEndpoint) {
        return createPulseTracker({
            clientId: CONFIG.tracking.provider,
            config: {
                autoCreateConsents: true,
                requireAdvertisingOptIn: true,
                deployStage: CONFIG.tracking.deployStage || 'dev',
                deployStageTpaas: CONFIG.tracking.deployStage || 'dev',
                adChoicesEnabled: !!CONFIG.tracking.adChoicesEnabled,
                stage: normalizeStage(CONFIG.tracking.deployStage || 'dev'),
                product: 'MyPage',
                version,
            },
            builders: {
                actor: actorBuilder,
            },
        });
    }

    if (config.sccTracking && config.sccTracking.baseEndpoint) {
        const { _scc_ } = window;

        if (!_scc_) {
            return null;
        }

        return createPulseTracker({
            clientId: CONFIG.tracking.provider,
            config: {
                consents: new Promise((resolve) => {
                    _scc_.subscribe('CMP:analytics', () => resolve(_scc_.getCachedOrDefaultConsentsForPulse()));
                }),
                requireAdvertisingOptIn: true,
                deployStage: CONFIG.tracking.deployStage || 'dev',
                deployStageTpaas: CONFIG.tracking.deployStage || 'dev',
                adChoicesEnabled: !!CONFIG.tracking.adChoicesEnabled,
                stage: normalizeStage(CONFIG.tracking.deployStage || 'dev'),
                product: 'MyPage',
                version,
            },
            builders: {
                actor: actorBuilder,
            },
        });
    }

    return null;
};

const tcfInit = () => initTracking('TCF', CONFIG.myPage.tcfTracking, tcf);
const sccInit = () => initTracking('SCC', CONFIG.myPage.sccTracking, scc);

export const pulseInit = async (getState) => {
    if (CONFIG.myPage.tcfTracking) {
        tcfInit();
    }

    if (CONFIG.myPage.sccTracking) {
        sccInit();
    }

    try {
        const pulseTracker = await createTracker(CONFIG.myPage, getState);

        if (!pulseTracker) {
            return;
        }

        pulseTracker.exposeToProxyClients?.();
        window.Tracker = pulseTracker;
        window.pulse = callback => callback(pulseTracker);
    } catch (error) {
        console.error('[Pulse] Initialization error:', error); // eslint-disable-line no-console
    }
};
